import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  List,
  Stack,
  Typography
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import classNames from 'classnames'
import { useAuth } from 'components/AuthProvider'
import useVault from 'hooks/useVault/useVault'
import { useCallback, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { MenuType } from './Menu'
import useStyles from './styles'

interface MenuListProps {
  navLinks: MenuType[]
  onClose: () => void
}

export default function MenuList({
  navLinks,
  onClose
}: Readonly<MenuListProps>) {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const location = useLocation()

  const { logout } = useAuth()
  const { activatedVaultId, handleActiveVault } = useVault()

  const [currentVault, setCurrentVault] = useState<string>(
    activatedVaultId.toString()
  )

  const redirectPath = useCallback(
    (path: string) => {
      navigate(path)
      onClose()
    },
    [navigate, onClose]
  )

  const handleClickSecondaryMenu = useCallback(
    async (secondaryMenu: MenuType) => {
      if (secondaryMenu.disable) return
      if (secondaryMenu.isVault) {
        setCurrentVault(secondaryMenu?.id)
        await handleActiveVault(secondaryMenu?.id).then(() => onClose())
      }

      if (secondaryMenu.href) {
        window.open(secondaryMenu.href, '_blank', 'noopener,noreferrer')
        onClose()
      } else {
        return secondaryMenu.path && redirectPath(secondaryMenu.path)
      }
    },
    [handleActiveVault, redirectPath, onClose]
  )

  return (
    <Box className={classes.menuListWrapper}>
      <List>
        {navLinks.map(navLink => {
          const { id, path, text, secondaryMenu, matchPath } = navLink ?? {}

          const isActive = matchPath ? matchPath(location.pathname) : false

          return (
            <Accordion
              key={id}
              disableGutters
              classes={{
                root: classes.listItemLink
              }}
              defaultExpanded={id === 'asset' || id === 'my_vault'}
            >
              <AccordionSummary
                expandIcon={undefined}
                aria-controls='panel1a-content'
                id='panel1a-header'
                className={classes.accordionSummary}
              >
                <Typography
                  className={classes.listItemText}
                  variant='h6Serif'
                  onClick={() => path && redirectPath(path)}
                  color={isActive ? 'primary' : 'black'}
                >
                  {text}
                </Typography>
              </AccordionSummary>
              {secondaryMenu && (
                <AccordionDetails className={classes.accordionDetails}>
                  {secondaryMenu.map(item => (
                    <Stack
                      key={item.id}
                      direction='row'
                      alignItems='center'
                      gap={0.5}
                      onClick={() => handleClickSecondaryMenu(item)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover svg': { color: 'primary.main' }
                      }}
                    >
                      <Typography
                        className={classNames(classes.listSecondaryItemText, {
                          [classes['listSecondaryItemText-disable']]:
                            item.disable
                        })}
                        variant='h6Serif'
                        color={
                          currentVault === item.id && item.isVault
                            ? 'primary'
                            : 'black'
                        }
                      >
                        {item.text}
                      </Typography>
                      {item.href && (
                        <OpenInNewIcon
                          sx={{ fontSize: '24px', color: 'black', mt: '5px' }}
                        />
                      )}
                    </Stack>
                  ))}
                </AccordionDetails>
              )}
            </Accordion>
          )
        })}
      </List>

      <Link
        className={classes.listItemLink}
        onClick={logout}
        underline='hover'
        sx={{ display: 'block', pb: 2, position: 'static' }}
        component='button'
      >
        <Typography
          className={classes.listItemText}
          component='p'
          variant='h6Serif'
        >
          Sign out
        </Typography>
      </Link>
    </Box>
  )
}
